import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Bot, Search, RefreshCw, X, AlertTriangle, Layout, Globe, Cpu, User, ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FOREST_GREEN } from '@/styles/tokens';
import {
  frameworkApi,
  registryApi,
  type FrameworkAgentSummary,
  type OrchestratorConfig,
} from '@/services/api/agentApi';
import { UserAgentProfile } from './UserAgentProfile';
import { FrameworkAgentDetail } from './FrameworkAgentDetail';
import * as errorUtils from '@/utils/errors';

interface AgentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof Bot; color: string; bg: string }> = {
  'Visual Editor': { label: 'Visual Editor', icon: Layout, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  'Domain': { label: 'Domain', icon: Globe, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  'Core': { label: 'Core', icon: Cpu, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  'Personal': { label: 'Personal', icon: User, color: 'text-primary', bg: 'bg-primary/10' },
  'Other': { label: 'Other', icon: Bot, color: 'text-muted-foreground', bg: 'bg-muted' },
};

const CATEGORY_ORDER = ['Core', 'Domain', 'Visual Editor', 'Personal', 'Other'];

type AgentModalTab = 'system' | 'user';
const AGENT_MODAL_TABS: AgentModalTab[] = ['system', 'user'];

interface AgentListItem {
  _id: string;
  id?: string;
  instanceId?: string;
  typeName?: string;
  name: string;
  status?: string;
  role?: string;
  description?: string;
  capabilities: string[];
  tier?: number;
  origin?: string;
  isSystem?: boolean;
  category?: string;
  createdAt?: string;
  lastActiveAt?: string;
  nextScheduledWake?: string;
}

const CATEGORY_SET = new Set(CATEGORY_ORDER);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getString(value: unknown, key: string): string | undefined {
  return isRecord(value) && typeof value[key] === 'string' ? value[key] : undefined;
}

function getNumber(value: unknown, key: string): number | undefined {
  return isRecord(value) && typeof value[key] === 'number' ? value[key] : undefined;
}

function normalizeCategory(category?: string): string {
  return category && CATEGORY_SET.has(category) ? category : 'Other';
}

function normalizeRegistryAgent(agent: OrchestratorConfig): AgentListItem {
  return {
    _id: agent._id,
    id: agent._id,
    typeName: getString(agent, 'typeName'),
    name: agent.name,
    status: agent.status,
    role: getString(agent, 'role'),
    description: getString(agent, 'description'),
    capabilities: agent.capabilities,
    tier: getNumber(agent, 'tier'),
    origin: getString(agent, 'origin') ?? 'system',
    isSystem: isRecord(agent) && typeof agent.isSystem === 'boolean' ? agent.isSystem : true,
    category: normalizeCategory(getString(agent, 'category')),
    createdAt: agent.createdAt,
  };
}

function isPersonalUserAgent(agent: AgentListItem): boolean {
  return agent.name === 'User Agent' || agent.typeName === 'UserAgent' || agent.role === 'user_agent';
}

function isClickableAgent(agent: AgentListItem): boolean {
  // The personal "User Agent" system agent is clickable
  if (isPersonalUserAgent(agent)) return true;
  // Framework user agents (non-system) are clickable
  if (agent.origin !== 'system' && !agent.isSystem) return true;
  return false;
}

function groupByCategory(agents: AgentListItem[]): Array<{ category: string; agents: AgentListItem[] }> {
  const groups = new Map<string, AgentListItem[]>();
  for (const agent of agents) {
    const category = normalizeCategory(agent.category);
    const group = groups.get(category);
    if (group) {
      group.push(agent);
    } else {
      groups.set(category, [agent]);
    }
  }
  return CATEGORY_ORDER
    .filter(category => (groups.get(category)?.length ?? 0) > 0)
    .map(category => ({ category, agents: groups.get(category) ?? [] }));
}

export const AgentsModal: React.FC<AgentsModalProps> = ({ open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState<AgentModalTab>('system');
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentListItem | null>(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [registryResult, frameworkResult] = await Promise.allSettled([
        registryApi.listAll(),
        frameworkApi.listAgents(),
      ]);

      if (registryResult.status === 'rejected' && frameworkResult.status === 'rejected') {
        throw registryResult.reason;
      }

      const registryAgents = registryResult.status === 'fulfilled'
        ? (registryResult.value?.data ?? []).map(normalizeRegistryAgent)
        : [];
      const frameworkAgents = frameworkResult.status === 'fulfilled'
        ? (frameworkResult.value?.data ?? []).map((agent: FrameworkAgentSummary): AgentListItem => ({
          ...agent,
          category: normalizeCategory(getString(agent, 'category')),
        }))
        : [];

      setAgents([...frameworkAgents, ...registryAgents]);

      if (registryResult.status === 'rejected') {
        setError(registryResult.reason?.message || 'Failed to load system agents');
      }
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load agents'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchAgents();
  }, [open, fetchAgents]);

  useEffect(() => {
    if (!open) {
      setActiveTab('system');
      setSearchInput('');
      setSelectedAgent(null);
    }
  }, [open]);

  const systemAgents = useMemo(() => agents.filter(a => a.origin === 'system' || a.isSystem), [agents]);
  const userAgents = useMemo(() => agents.filter(a => a.origin !== 'system' && !a.isSystem), [agents]);

  const filtered = useMemo(() => {
    const display = activeTab === 'system' ? systemAgents : userAgents;
    if (!searchInput.trim()) return display;
    const q = searchInput.toLowerCase();
    return display.filter(
      (a) =>
        a.name?.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q) ||
        a.role?.toLowerCase().includes(q) ||
        a.category?.toLowerCase().includes(q),
    );
  }, [activeTab, systemAgents, userAgents, searchInput]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  const renderAgentCard = (agent: AgentListItem) => {
    const category = normalizeCategory(agent.category);
    const catConfig = CATEGORY_CONFIG[category];
    const clickable = isClickableAgent(agent);

    return (
      <div
        key={agent._id || agent.id || agent.name}
        className={`border border-border rounded-lg p-3 transition-colors ${
          clickable
            ? 'hover:border-primary/40 hover:bg-primary/5 cursor-pointer'
            : 'hover:border-border/80'
        }`}
        onClick={clickable ? () => setSelectedAgent(agent) : undefined}
      >
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-md bg-primary/10 mt-0.5">
            <Bot className="h-4 w-4" style={{ color: FOREST_GREEN.DEFAULT }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground truncate">{agent.name}</span>
              {agent.category && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${catConfig.bg} ${catConfig.color}`}>
                  {category}
                </span>
              )}
              {agent.tier != null && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Tier {agent.tier}
                </span>
              )}
              {agent.status && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  agent.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {agent.status}
                </span>
              )}
            </div>
            {agent.role && (
              <p className="text-xs text-muted-foreground mt-0.5">{agent.role}</p>
            )}
            {agent.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{agent.description}</p>
            )}
            {agent.capabilities?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {agent.capabilities.slice(0, 4).map((cap: string) => (
                  <span key={cap} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                    {cap}
                  </span>
                ))}
                {agent.capabilities.length > 4 && (
                  <span className="text-[10px] text-muted-foreground">+{agent.capabilities.length - 4}</span>
                )}
              </div>
            )}
          </div>
          {clickable && (
            <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
          )}
        </div>
      </div>
    );
  };

  const renderAgentList = (tab: AgentModalTab) => {
    if (loading && agents.length === 0) {
      return (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-muted-foreground py-16 px-8">
          <Bot className="w-12 h-12 mb-3 opacity-40" />
          <h3 className="text-base font-medium text-foreground mb-1">No Agents Found</h3>
          <p className="text-sm text-center max-w-sm">
            {searchInput ? 'No agents match your search.' : 'No agents available in this category.'}
          </p>
        </div>
      );
    }

    // System tab: group by category with section headers
    if (tab === 'system' && !searchInput.trim() && grouped.length > 1) {
      return (
        <div className="p-4 space-y-4">
          {grouped.map(({ category, agents: catAgents }) => {
            const catConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG['Other'];
            const Icon = catConfig.icon;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-3.5 w-3.5 ${catConfig.color}`} />
                  <span className={`text-xs font-medium ${catConfig.color}`}>{catConfig.label}</span>
                  <span className="text-[10px] text-muted-foreground">({catAgents.length})</span>
                </div>
                <div className="grid gap-2 grid-cols-1">
                  {catAgents.map(renderAgentCard)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // User tab or filtered: flat list
    return (
      <div className="p-4 grid gap-3 grid-cols-1">
        {filtered.map(renderAgentCard)}
      </div>
    );
  };

  const renderDetailView = () => {
    if (!selectedAgent) return null;
    const isPersonal = isPersonalUserAgent(selectedAgent);

    return (
      <div className="h-[75vh] flex flex-col">
        {/* Back header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <button
            onClick={() => setSelectedAgent(null)}
            className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to agents
          </button>
          <span className="text-muted-foreground">|</span>
          <span className="text-sm font-medium text-foreground truncate">{selectedAgent.name}</span>
        </div>

        {/* Detail content */}
        <div className="flex-1 overflow-y-auto">
          {isPersonal ? (
            <UserAgentProfile compact />
          ) : (
            <FrameworkAgentDetail agent={selectedAgent} />
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-5xl p-0 gap-0">
        {selectedAgent ? (
          renderDetailView()
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Bot className="h-5 w-5" style={{ color: FOREST_GREEN.DEFAULT }} />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  Agents
                  {agents.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground ml-1.5">({agents.length})</span>
                  )}
                </h2>
                <p className="text-[11px] text-muted-foreground">Browse system and user-created agents</p>
              </div>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                if (value === 'system' || value === 'user') {
                  setActiveTab(value);
                }
              }}
            >
              <div className="border-b border-border px-4 pb-3">
                <TabsList className="w-full">
                  <TabsTrigger value="system" className="flex-1 text-xs">
                    System Agents
                    {systemAgents.length > 0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground">({systemAgents.length})</span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="user" className="flex-1 text-xs">
                    User Agents
                    {userAgents.length > 0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground">({userAgents.length})</span>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              {AGENT_MODAL_TABS.map((tab) => (
                <TabsContent key={tab} value={tab} className="data-[state=active]:h-[75vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
                  {/* Toolbar */}
                  <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={fetchAgents} disabled={loading}>
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <div className="relative flex-1 min-w-[160px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search agents..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      {searchInput && (
                        <button
                          onClick={() => setSearchInput('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  {renderAgentList(tab)}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
